// src/components/TagList.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, addDoc, deleteDoc, doc, query, where } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { X } from 'lucide-react';
import { decryptIfNeeded, encrypt } from '../utils/encryption';

function TagList() {
  const [tags, setTags] = useState([]);
  const [newTagName, setNewTagName] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        fetchTags(currentUser.uid);
      } else {
        setTags([]);
        setError('Please login to view tags');
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const fetchTags = async (uid) => {
    if (!uid) return;
    try {
      const tagsRef = collection(db, 'tags');
      const q = query(tagsRef, where('userId', '==', uid));
      const querySnapshot = await getDocs(q);
      const encryptionKey = process.env.REACT_APP_ENCRYPTION_KEY;
      const tagsList = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          name: decryptIfNeeded(data.name, encryptionKey)
        };
      });
      setTags(tagsList);
    } catch (error) {
      console.error('Error fetching tags:', error);
      setError('Failed to fetch tags');
    }
  };

  const handleCreateTag = async (e) => {
    e.preventDefault();
    if (!user) {
      setError('Please login to create tags');
      return;
    }
    if (!newTagName.trim()) {
      setError('Tag name cannot be empty');
      return;
    }

    try {
      setLoading(true);
      const encryptionKey = process.env.REACT_APP_ENCRYPTION_KEY;
      await addDoc(collection(db, 'tags'), {
        name: encrypt(newTagName.trim(), encryptionKey),
        userId: user.uid,
        createdAt: new Date()
      });
      setNewTagName('');
      fetchTags(user.uid);
    } catch (error) {
      console.error('Error creating tag:', error);
      setError('Failed to create tag');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTag = async (tagId) => {
    if (!user) return;
    if (!window.confirm('Are you sure you want to delete this tag?')) return;

    try {
      await deleteDoc(doc(db, 'tags', tagId));
      fetchTags(user.uid);
    } catch (error) {
      console.error('Error deleting tag:', error);
      setError('Failed to delete tag');
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Tags</h2>
      {error && (
        <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 rounded text-sm">
          {error}
        </div>
      )}
      
      <form onSubmit={handleCreateTag} className="mb-6">
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex-1 w-full sm:w-auto">
            <input
              type="text"
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              placeholder="Enter new tag name"
              disabled={loading || !user}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50"
            />
          </div>
          <button
            type="submit"
            disabled={loading || !user}
            className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-6 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            Add Tag
          </button>
        </div>
      </form>

      {loading ? (
        <p className="text-gray-600 dark:text-gray-400">Loading tags...</p>
      ) : (
        <div className="flex flex-wrap gap-2 mt-4">
          {tags.map((tag) => (
            <span
              key={tag.id}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-primary/20 text-primary border border-primary/30"
            >
              {tag.name}
              <button
                onClick={() => handleDeleteTag(tag.id)}
                className="hover:bg-primary/30 rounded-full p-0.5 transition-colors"
                aria-label={`Delete ${tag.name}`}
              >
                <X className="w-4 h-4" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default TagList;